import React, { useEffect, useRef, useState } from 'react'
import { Button, Card, CardBody, Modal } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { CastType, IQuery, IWhere, IQuerySelectData, IQueryTableData } from '../solution/Interfaces';
import ShowErrorAlert from '../ShowErrorAlert';
import { formatDateFull } from '../solution/utils/AppUtil';
import { Check, FileText, Pencil, Printer, Save, ScanLine, X } from 'lucide-react';
import Logo from "../../assets/img/logo.png";
import bkLogo from "../../assets/img/birkenstock_logo.png";
import hdLogo from "../../assets/img/hey_dude.png";
import tbLogo from "../../assets/img/ted_baker.png";
import dcLogo from "../../assets/img/digicape_logo_black.png";
import htLogo from "../../assets/img/hi-tec.svg"; 
import crLogo from "../../assets/img/crocs_logo_new.png";
import okLogo from "../../assets/img/oakley_logo.jpeg";


interface IStore {
    id: string;
    code: string;
    name: string;
    email: string
    customer_code: string;
    costing_code: string;
    sit_location: string;
    shopify_location_id: string;
    created: string;
    updated: string| null;
    address?: string; 
}

interface Props {
    store: IStore;
    onBack: () => void;
    allStores: IStore[]
}

interface INormalIBT {
     id: string;
     created: string;
     created_by: string;
     updated: string | null;
     doc_num: string,
     category: string,
     internal: boolean;
     from_whs: string;
     notes: string | null;
     status: string;
     to_whs: string;
}

interface INormalIBTLine {
    id: string;
    created: string;
    updated: string | null;
    line_num: number;
    item_name: string;
    item_code: string;
    item_barcode: string;
    doc_num: string;
    qty_on_hand: number | null;
    qty_received: number | null;
    qty_scanned_out: number | null;
    qty_scanned_in: number | null;
    qty_sent: number | null;
}

interface ISelectedScannedItem {
  id: string;
  barcode: string;
  item_code: string;
  item_group: string;
  name: string;
  product_code: string;
}

export const NormalIBT: React.FC<Props> = ({ store, onBack, allStores }) => {

  const { fetchData, responseErrorMessage, showErrorAlert, responseStatus }  = useAuth();

  const queryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/data"}`;
  const queryUrlSelectNoCount: string = `${process.env.REACT_APP_BASE_URL}/${"query/select/data"}`;
  const fetchNormalIBTActionsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/INTERBRANCH-TRANSFER-ACTIONS/run`; 
  const fetchNormalIBTLineActionsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/INTERBRANCH-TRANSFER-LINE-ACTIONS/run`; 
  const queryUrlTable: string = `${process.env.REACT_APP_BASE_URL}/${"query/table/data"}`;

  const [normalIBTState, setNormalIBTState] = useState<INormalIBT[]>([]);
  const [normalIBTLineState, setNormalIBTLineState] = useState<INormalIBTLine[]>([]);
  const [selectedNormalIBT, setSelectedNormalIBT] = useState<INormalIBT | null>(null);
  const [selectedToWharehouseStore, setSelectedToWharehouseStore] = useState<IStore | null>(null);
  const [stores, setStores] = useState<IStore[]| null>(null);
  const [selectedDocNum, setSelectedDocNum] = useState<string>("");
  const [totalQtySent, setTotalQtySent] = useState<number>(0);

  const [submitRefreshData, setSubmitRefreshData] = useState<boolean>(false); 
  const [submitRefreshLineData, setSubmitRefreshLineData] = useState<boolean>(false);

  const [selectedStore, setSelectedStore] = useState("");
  const [normalIBTNotes, setNormalIBTNotes] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [viewMode, setViewMode] = useState<'scan' | 'view' | 'print' | 'default'>('default'); 
  const [loading, setLoading] = useState<boolean>(false);

  const [showCreateNormalIBTModal, setShowCreateNormalIBTModal] = useState<boolean>(false);
  const [showUpdateStatusNormalIBTModal, setShowUpdateStatusNormalIBTModal] = useState<boolean>(false);
  const [showDeleteNormalIBTModal, setShowDeleteNormalIBTModal] = useState<boolean>(false);

  //refs 
  const fileRef = useRef<HTMLInputElement | null>(null);
  const scanRef = useRef<HTMLInputElement | null>(null);
  const storeRef = useRef<HTMLSelectElement | null>(null);

  const [selectedScannedItem, setSelectedScannedItem] = useState<ISelectedScannedItem | null>(null);  
  const [showCustomQuantitySection, setShowCustomQuantitySection] = useState<boolean>(false);
  const [customQuantity, setCustomQuantity] = useState<string>('1');

  const [printTarget, setPrintTarget] = useState<'label' | 'manifest'>('label'); 

  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editedQty, setEditedQty] = useState<Record<string, number>>({}); 
  const [inputDraft, setInputDraft] = useState<Record<string, string>>({});

  const defaultWhere: IWhere[] = [
    {
        fullColumnName: "from_whs",
        value: store?.code,
        operator: "EQ",
        cast: CastType.STRING
    },
    {
        fullColumnName: "internal",
        value: false,
        operator: "EQ",
        cast: CastType.BOOLEAN
    },
  ];

   const [queryState, setQueryState] = useState<IQuery>({
        tableName: "sa_ares_interbranch_transfer",
        select: [],
        groupBy: [], 
        join: [],
        orderBy: [],
        where: defaultWhere,
        mainTableOnly: false,
        includeExtraColumns: true,
        includeControlColumns: true,
        modifiedAfter: null,
        touchedBefore: null,
        isDirty: null,
        pageNum: 0,
        pageSize: 100,
        frontEndOnly: null
   });


    //Query Line State
    const [queryLineState, setQueryLineState] = useState<IQuery>({
        tableName: "sa_ares_interbranch_transfer_line",
        select: [],
        groupBy: [],
        join: [{
          relationship: 'item',
          joinType: "LEFT"
        }],
        orderBy: [ 
            {
                fullColumnName: "created",
                direction: "ASC",
                cast: CastType.TIMESTAMP
            }
        ],
        where:[],
        mainTableOnly: false,
        includeExtraColumns: true,
        includeControlColumns: true,
        modifiedAfter: null,
        touchedBefore: null,
        isDirty: null,
        // pageNum: 0,
        // pageSize: 100,
        frontEndOnly: null
    });

    const storeQuery: IQuery = {
        tableName: "sa_ares_store",
        select: [
    
        ],
        groupBy: [],
        join: [],
        orderBy: [ 
            {
                fullColumnName: "name",
                direction: "ASC",
                cast: CastType.STRING
            }
        ],
        where:[],
        mainTableOnly: false,
        includeExtraColumns: true,
        includeControlColumns: true,
        modifiedAfter: null,
        touchedBefore: null,
        isDirty: null,
        pageNum: 0,
        pageSize: 100,
        frontEndOnly: null
    } 

    const defaultQueryState: IQuerySelectData = {
        appCode: "ares",
        selectCode: "scan-item",  
        params: {}
    };   
    
      
    const [querySelectState, setQuerySelectState] = useState<IQuerySelectData>(defaultQueryState);

    const defaultQueryTableSelectStores: IQueryTableData = {
        tableName: "sa_ares_store",
       
    }

     useEffect(() => {
          setLoading(true); 
          fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryState)}, processData, cleanUpLoader);
     }, [queryState, submitRefreshData]);
    
    
    useEffect(() => {
        if (showCreateNormalIBTModal && storeRef.current) {
            storeRef.current.focus();
        }
    }, [showCreateNormalIBTModal]);

    useEffect(() => {
        if (viewMode === "scan" && scanRef.current) {
            scanRef.current.focus();
        }
    }, [viewMode]);
    
    useEffect(() => {
    const queryLine = {...queryLineState}
    queryLine.where = [
            {
            fullColumnName: "doc_num",
            value: selectedNormalIBT?.doc_num, 
            operator: "EQ",
            cast: CastType.STRING
        }       
    ]
    setLoading(true); 
    fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryLine)}, processLineData, cleanUpLoader);
    }, [selectedNormalIBT, submitRefreshLineData]);

    useEffect(() => {
        if(viewMode === "print"){
            const queryStore = {...storeQuery}
            queryStore.where = [
                    {
                    fullColumnName: "code",
                    value: selectedNormalIBT?.to_whs, 
                    operator: "EQ",
                    cast: CastType.STRING
                }       
            ]
            setLoading(true); 
            fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryStore)}, processStoreData, cleanUpLoader);
        }
    }, [selectedNormalIBT]);

    useEffect(() => {
        fetchData(queryUrlTable, {method:"POST", body: JSON.stringify(defaultQueryTableSelectStores)}, processAllStoreData);
    }, []);


    const processData = (result: any) => {

      const normalIBT: INormalIBT[] = [];

      for(const key in result?.data){
          normalIBT.push({...result.data[key].data, ...result.data[key].control, ...result.data[key].undefined});
      }

      // Sort by created date descending
      normalIBT.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());

      setNormalIBTState(normalIBT);
    }


    const processLineData = (result: any) => {

        const normalIBTLines: INormalIBTLine[] = [];
         let total = 0;

        for(const key in result?.data){

            const entry = result.data[key];
            const control = entry.control || {};
            const data = entry.data || {};

            const qtySent = data?.qty_sent ?? 0;

            normalIBTLines.push({
                id: control.id,
                created: control.created,
                updated: control.updated ?? null,
                line_num: data.line_num,
                item_name: data["item.name"],
                item_code: data.item_code,
                item_barcode: data["item.barcode"], 
                doc_num: data.doc_num,
                qty_on_hand: data.qty_on_hand,
                qty_received: data.qty_received,
                qty_scanned_out: data.qty_scanned_out,
                qty_scanned_in: data.qty_scanned_in,
                qty_sent: data.qty_sent,
                });

                total += qtySent;
        }
        
        setNormalIBTLineState(normalIBTLines);
        setTotalQtySent(total); 
    };

    
    const processStoreData = (result: any) => {
        const firstKey = Object.keys(result?.data || {})[0];

        if (firstKey) {
            const store: IStore = {
            ...result.data[firstKey].data,
            ...result.data[firstKey].control,
            ...result.data[firstKey].undefined,
            };

            setSelectedToWharehouseStore(store);
        } else {
            setSelectedToWharehouseStore(null); // fallback if no data
        }
    };


    const processAllStoreData = (result: any) => {

        const storeAccess: IStore[] = [];

        for (const entry of result?.data || []) {

            const control = entry.control || {};
            const data = entry.data || {};

            storeAccess.push({
                id: entry.id,
                code: data.code,
                name: data.name,
                email: data.email,
                customer_code: data.customer_code,
                costing_code: data.costing_code,
                sit_location: data.sit_location,
                shopify_location_id: data.shopify_location_id,
                created: control.created,
                updated: control.updated,
                address: data.address,
            });
        }

        storeAccess.sort((a, b) => a.name.localeCompare(b.name));

        setStores(storeAccess);
    };


    //default clean up loader
    const cleanUpLoader = () => {
        setLoading(false);
    }

    // Handle file/folder selection
    const handleFolderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]; // Select the first file (if any)
        if (file) {
            setSelectedFile(file); // Set the selected file
        } else {
            setSelectedFile(null); // Handle case where no file is selected
        }
    };

  // Handle Create Normal IBT
   const handleCreateNormalIBTSubmit = () => {
        if (store.code && selectedStore !== "" && !selectedFile) {
            setLoading(true);
            fetchData(fetchNormalIBTActionsQueryUrl, 
            {method:"POST", body: JSON.stringify({ store_code: store.code, to_store_code: selectedStore, notes: normalIBTNotes })}, 
            (result: any, status: string) =>  { 
                if(status === "200"){
                    setSelectedNormalIBT(result);
                    setViewMode("scan");
                }
            }, cleanUpLoader);

        }else if (store.code && selectedStore !== "" && selectedFile) {

            // how to do a multipe part form posts javascript  
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('j1', new Blob([JSON.stringify({ store_code: store.code, to_store_code: selectedStore, notes: normalIBTNotes })], { type: 'application/json' }));

            const headers = new Headers();
            headers.append("Content-Type", "multipart/formdata");

            setLoading(true);
            fetchData(fetchNormalIBTActionsQueryUrl, 
            {method:"POST", body: formData, headers: headers }, 
            (result: any, status: string) =>  { 
                if(status === "200"){
                   setSubmitRefreshData(prev => !prev);
                }
            }, cleanUpLoader);
            
        }else{
            return; // Prevent further execution if the code is not available
        }

        // Reset fileRef after submission
        setSelectedFile(null);
        if (fileRef.current) {
            fileRef.current.value = ''; 
        }

        setNormalIBTNotes("");
        setSelectedStore("");
   }

    //handle view button when clicked
    const handleViewClick = (normalIBT : INormalIBT) => {
        setViewMode('view'); // Switch to view mode
        setSelectedNormalIBT(normalIBT);
    }

    //handle scan button when clicked
    const handleScanClick = (normalIBT : INormalIBT) => {
        setViewMode('scan'); // Switch to Scan mode
        setSelectedNormalIBT(normalIBT);
    }

     //handle print button when clicked
    const handlePrintClick = (normalIBT : INormalIBT) => {
        setViewMode('print'); // Switch to print mode
        setSelectedNormalIBT(normalIBT);
    }

    // Add the event handler for "Enter" key press
    const handleScanKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, docNum: string) => {
          if (event.key === 'Enter') {
              event.preventDefault();  // Prevent default behavior (if needed)
              handleScanSubmit(docNum); 
          }
    };


    const handleScanSubmit = (docNum: string) => {

        setLoading(true);
        const scanValueItemCode = scanRef.current?.value.trim();
        if (!docNum || !scanValueItemCode) return;

        const queryPayload = {
          ...querySelectState,
          params: { ...querySelectState.params, scan_code: scanValueItemCode },
        };

        let shouldRunLineFetch = true;

        fetchData( queryUrlSelectNoCount,
          { method: "POST", body: JSON.stringify(queryPayload)},
          (result: any, status: string) => {
            if (status === "200") {
              const item = result?.data?.[0]?.data;
              const id = result?.data?.[0]?.id;

              if (item && id) {

                const itemGroup = item.item_group;

                const scannedItem: ISelectedScannedItem = {
                  id,
                  barcode: item.barcode,
                  item_code: item.item_code,
                  item_group: itemGroup,
                  name: item.name,
                  product_code: item.product_code,
                };

                setSelectedScannedItem(scannedItem);

                if (itemGroup === "Jibbitz") {
                  setShowCustomQuantitySection(true);
                  shouldRunLineFetch = false; // Don't run line fetch for Jibbitz
                }
              }
            }

            // Only run line fetch if allowed
            if (shouldRunLineFetch) {
             fetchData(fetchNormalIBTLineActionsQueryUrl,
              { method:"POST", body: JSON.stringify({ doc_num: docNum, item_code: scanValueItemCode, qty_scanned_out: 1 })}, 
              (result: any, status: string) =>  { 
                if(status === "200"){
                   setSubmitRefreshLineData(prev => !prev); 
                }
              }, cleanUpLoader);
            } 
            else {
              cleanUpLoader(); // still clean up if skipping line fetch
            }
          }
        );

        if (scanRef.current) {
          scanRef.current.value = "";
        }
    };


     const handleQuantitySubmit = () => {

      if (!selectedScannedItem || !selectedNormalIBT?.doc_num) return;

      const { item_code, barcode } = selectedScannedItem;

      const parsedQty = Math.max(1, parseInt(customQuantity) || 1);

      const payload = {
        doc_num: selectedNormalIBT.doc_num,
        qty_scanned_out: parsedQty,
        item_code: item_code || barcode,
      };

      setLoading(true);

      fetchData(fetchNormalIBTLineActionsQueryUrl,
          { method:"POST", body: JSON.stringify(payload)}, 
          (result: any, status: string) =>  { 
            if(status === "200"){
                 setShowCustomQuantitySection(false);
                 setSelectedScannedItem(null);
                 setCustomQuantity('1');
                 setSubmitRefreshLineData(prev => !prev); 
            }
          }, cleanUpLoader);
    };


    //handle to send normal ibt button when clicked
    const handleToSendStatusNormalIBTClick = (normalIBT : INormalIBT) => {
        setViewMode('default'); // Switch to default mode
        setShowUpdateStatusNormalIBTModal(true);
        setSelectedDocNum(normalIBT.doc_num);
    }

    //handle the submit for setting to send normal ibt
    const handleToSendNormalIBTSubmit = () => {
        if(selectedDocNum !== ""){
            setLoading(true);
            fetchData(fetchNormalIBTActionsQueryUrl, 
                {method:"POST", body: JSON.stringify({ doc_num: selectedDocNum,  status: "TO_SEND" })}, 
                (result: any, status: string) =>  {
                    if(status === "200"){
                       // Causes refresh on data  
                       setSubmitRefreshData(prev => !prev);
                    }
                }, cleanUpLoader);
            setSelectedDocNum("");
        }else{
            return; 
        }
    }


    const handleDeleteNormalIBTClick = (normalIBT : INormalIBT) => {
        setViewMode('default'); // Switch to default mode
        setShowDeleteNormalIBTModal(true);
        setSelectedDocNum(normalIBT.doc_num);
    }

    const handleDeleteNormalIBTSubmit = () => {
        if(selectedDocNum !== ""){
            setLoading(true);
            fetchData(fetchNormalIBTActionsQueryUrl, 
                {method:"POST", body: JSON.stringify({ doc_num: "",  status: "DELETED" })}, 
                (result: any, status: string) =>  {
                    if(status === "200"){
                      // Causes refresh on data  
                      setSubmitRefreshData(prev => !prev);
                    }
                }, cleanUpLoader);
                setSelectedDocNum("");
        }else{
            return; 
        }
    }


    const handleEditClick = (id: string, initialQty: number) => {
        setEditingRowId(id);
        setEditedQty(prev => ({ ...prev, [id]: initialQty }));
        setInputDraft(prev => ({ ...prev, [id]: initialQty.toString() }));
    };

    const handleQtyDraftChange = (id: string, value: string) => {
        setInputDraft(prev => ({ ...prev, [id]: value }));
    };

    const handleBlur = (id: string) => {
        const draftValue = inputDraft[id];
        const parsed = parseInt(draftValue, 10);
        setEditedQty(prev => ({ ...prev, [id]: isNaN(parsed) ? 0 : parsed }));
    };

    const handleSaveClick = (id: string) => {
        const qty = editedQty[id];
        const item_code = normalIBTLineState.find(line => line.id === id)?.item_code;
        const doc_num = selectedNormalIBT?.doc_num;

        if (!item_code || qty === undefined || doc_num === undefined) return;

        const payload = {
        doc_num,
        item_code,
        qty_scanned_out: qty,
        mode: 'exact'
        };


        // Perform your save operation here (e.g., API call)
        fetchData(fetchNormalIBTLineActionsQueryUrl,
        { method:"POST", body: JSON.stringify(payload)}, 
        (result: any, status: string) =>  { 
            if(status === "200"){
                setSubmitRefreshLineData(prev => !prev); 
                setEditingRowId(null); // exit edit mode
                setEditedQty({});
                setInputDraft({});
            }
        }, cleanUpLoader);
    };



    const DisabledScanBtn = () => {
        return (
            <span className="btn h-100 d-flex align-items-center gap-1" title="Scan"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
              >
              <ScanLine style={{ width: "20px", height: "20px", color: "#6c757d" }} />
            </span>
        ) 
    } 

    const DisabledCheckBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Submit"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
         <Check style={{ width: "20px", height: "20px", color: "#6c757d" }} />
        </span>
       )
    } 

    const DisabledDeleteBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Set To Delete"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
          <X style={{ width: "20px", height: "20px", color: "#6c757d" }} />
        </span>
        )
    } 

    const DisabledPrintBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Print"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
         <Printer style={{ width: "20px", height: "20px", color: "#6c757d" }} />
        </span>
       )
    } 


    const DisabledEditQtyBtn = () => {
            return (
            <span className="btn h-100 d-flex align-items-center gap-1" title="Edit Quantity"
                style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
            >
            <Pencil style={{ width: "20px", height: "20px", color: "#6c757d" }} />
            </span>
        )
    } 

    const DisabledSaveQtyBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Save Quantity"
                style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
            >
            <Save style={{ width: "22px", height: "22px", color: "#6c757d" }} />
        </span>
        )
    } 


    const resetNormalIBTModal = () => {

        setShowCreateNormalIBTModal(false);
        setSelectedStore("");
        setNormalIBTNotes("");
        setSelectedFile(null);
 
        // Clear the file input manually via ref
        if (fileRef.current) {
          fileRef.current.value = "";
        }
    };

    //handles the back to normal ibt.
    const handleBackToNormalIBTS = () => {
        setViewMode('default');
        setSelectedNormalIBT(null);
        setTotalQtySent(0); 
        setSelectedScannedItem(null);
        setCustomQuantity('1');
        setShowCustomQuantitySection(false); 
        setSelectedToWharehouseStore(null);
        setPrintTarget('label');
        setEditingRowId(null); // exit edit mode
        setEditedQty({});
        setInputDraft({});
        fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryState)}, processData, cleanUpLoader);
    }

    const handleCloseForm = () => {
      setSelectedScannedItem(null);
      setCustomQuantity('1');
      setShowCustomQuantitySection(false); 
    };


    const { src: customerLogo, width: customerLogoWidth } = getCustomerLogo();

    function getCustomerLogo() {
        switch (process.env.REACT_APP_COMPANY) {
        case "ares-bk":
            return { src: bkLogo, width: "150px" };
        case "ares-hd":
            return { src: hdLogo, width: "100px" }; // 80
        case "ares-tb":
            return { src: tbLogo, width: "150px" };
        case "digicape":
            return { src: dcLogo, width: "80px" };
        case "hi-tec":
            return { src: htLogo, width: "80px" };
        case "ares-cr":
            return { src: crLogo, width: "160px" };
        case "ares-ok":
            return { src: okLogo, width: "100px" };
        default:
            return { src: Logo, width: "80px" };
        }
    }

  const thStyle = {
      backgroundColor: '#f7f5f5',
      border: '1px solid #deebed',
      fontWeight: 600,
      padding: '10px 8px',
      fontSize: '0.85rem',
  };


   const handlePrint = () => {
       window.print();
   }

   
   //determine address lines
   const addressLines = selectedToWharehouseStore?.address?.split("\\r") || [];
   
  return (
    <>
    { showErrorAlert && <ShowErrorAlert message={responseErrorMessage} /> } 
    <Card>
     <Card.Header>
         <Card.Title>{`Send Stock (IBT) [${store.name}]`}</Card.Title>                       
     </Card.Header>
     <CardBody>
        <div className="d-flex gap-1">
            <button
                className="btn btn-secondary h-100"
                onClick={() => {
                    if (viewMode === 'default') {
                        onBack();
                    } else {
                        handleBackToNormalIBTS();
                    }
                    }}
                >
                { viewMode === 'default' ? "Back To Stock Transfers (IBT)" : "Back To Send Stock IBT" }
            </button>
            {viewMode === 'default' && (
                <button
                className="btn btn-secondary h-100"
                onClick={() => setShowCreateNormalIBTModal(true)}
                >
                 Create Send Stock (IBT)
            </button>
            )}
        </div>
        <br></br>                                         
           <div>
                  { /* Internal IBT Table Start */ }
                  <table className="table table-bordered">
                            <thead>                           
                                <tr>
                                    <th style={thStyle}>Doc Number</th>
                                    <th style={thStyle}>From Warehouse</th>
                                    <th style={thStyle}>To Warehouse</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={thStyle}>Created</th>
                                    <th style={thStyle}>Notes</th>
                                    <th style={thStyle}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                               { loading && viewMode === "default" ? (
                                 <>
                                    <tr>
                                        <td colSpan={6} className="text-center">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        </td>
                                    </tr>
                                 </>
                                 ) : (
                                    <>
                                     {!selectedNormalIBT && viewMode === "default" && normalIBTState.map((normalIBT: INormalIBT) => (
                                      <tr key={normalIBT.id}>
                                        <td>{normalIBT.doc_num}</td>
                                        <td>{normalIBT.from_whs}</td>
                                        <td>{normalIBT.to_whs}</td>
                                        <td>{normalIBT.status}</td>
                                        <td>{formatDateFull(normalIBT.created)}</td>
                                        <td title={normalIBT?.notes ?? ""}>
                                            <div style={{ 
                                                whiteSpace: "nowrap", 
                                                overflow: "hidden", 
                                                textOverflow: "ellipsis", 
                                                maxWidth: "150px" 
                                            }}>
                                                {normalIBT.notes}
                                            </div>
                                        </td>
                                        <td className="d-flex gap-1">

                                            {normalIBT.status === "OPEN" ? (
                                                <button
                                                    className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                                    title="Scan"
                                                    onClick={() => handleScanClick(normalIBT)}
                                                >
                                                    <ScanLine style={{ width: "20px", height: "20px" }} />
                                                </button>
                                                ) : (
                                                <DisabledScanBtn />
                                            )}

                                           {normalIBT.status === "OPEN" ? (
                                                <button
                                                    className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                                    title="Submit"
                                                    onClick={() => handleToSendStatusNormalIBTClick(normalIBT)}
                                                >
                                                    <Check style={{ width: "20px", height: "20px" }} />
                                                </button>
                                            ) : (
                                                 <DisabledCheckBtn />
                                            )}

                                            <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='View'
                                                onClick={() => handleViewClick(normalIBT)}
                                            >
                                                <FileText style={{ width: "20px", height: "20px" }}  /> 
                                            </button>

                                            {(normalIBT.status === "OPEN") ? (
                                                 <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='Set To Delete'
                                                   onClick={() => handleDeleteNormalIBTClick(normalIBT)}
                                                  >
                                                  <X style={{ width: "20px", height: "20px" }}  /> 
                                                 </button>
                                                ) : (
                                                <DisabledDeleteBtn/>
                                            )}  

                                             {normalIBT.status === "CLOSED" ? (
                                                <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='Print'
                                                 onClick={() => handlePrintClick(normalIBT)}
                                                >
                                                <Printer style={{ width: "20px", height: "20px" }}  /> 
                                                </button>
                                            ) : (
                                                <DisabledPrintBtn />
                                            )}
                                                                                        
                                        </td>
                                     </tr>
                                   ))}
                                 </>
                                )}

                                {selectedNormalIBT  && viewMode !== "default" &&
                                    <>
                                        <tr key={selectedNormalIBT.id}>
                                            <td>{selectedNormalIBT.doc_num}</td>
                                            <td>{selectedNormalIBT.from_whs}</td>
                                            <td>{selectedNormalIBT.to_whs}</td>
                                            <td>{selectedNormalIBT.status}</td>
                                            <td>{formatDateFull(selectedNormalIBT.created)}</td>
                                            <td title={selectedNormalIBT?.notes ?? ""}>
                                            <div style={{ 
                                                whiteSpace: "nowrap", 
                                                overflow: "hidden", 
                                                textOverflow: "ellipsis", 
                                                maxWidth: "150px" 
                                            }}>
                                                {selectedNormalIBT.notes}
                                            </div>
                                          </td>
                                           <td>
                                          </td>
                                        </tr>
                                        <tr>
                                            <td colSpan={9} style={{ textAlign: 'center' }}>
                                                  { viewMode === 'view' || viewMode === 'print' ? (null) : (
                                                    <>
                                                        {selectedScannedItem && showCustomQuantitySection ? (
                                                            <form
                                                            onSubmit={(e) => {
                                                                e.preventDefault();
                                                                handleQuantitySubmit();
                                                            }}
                                                            className="p-3 border rounded bg-light"
                                                            style={{ display: 'inline-block', textAlign: 'left', minWidth: '600px' }}
                                                            >
                                                            <div className="mb-3 position-relative">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                <h6 className="mb-0" style={{ fontWeight: 600 }}>Scanned Item Details</h6>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-secondary btn-sm rounded-circle d-flex align-items-center justify-content-center"
                                                                    style={{ width: '28px', height: '28px' }}
                                                                    onClick={handleCloseForm}
                                                                    aria-label="Close"
                                                                >
                                                                    <X size={16} />
                                                                </button>
                                                                </div>
                                                                <hr className="my-2" />
        
                                                                <div className="row">
                                                                <div className="col-md-6 mb-2">
                                                                    <label className="form-label mb-0"><strong>Name:</strong></label>
                                                                    <div className="form-control-plaintext">{selectedScannedItem.name}</div>
                                                                </div>
                                                                <div className="col-md-6 mb-2">
                                                                    <label className="form-label mb-0"><strong>Item Group:</strong></label>
                                                                    <div className="form-control-plaintext">{selectedScannedItem.item_group}</div>
                                                                </div>
                                                                <div className="col-md-6 mb-2">
                                                                    <label className="form-label mb-0"><strong>Item Code:</strong></label>
                                                                    <div className="form-control-plaintext">{selectedScannedItem.item_code}</div>
                                                                </div>
                                                                <div className="col-md-6 mb-2">
                                                                    <label className="form-label mb-0"><strong>Barcode:</strong></label>
                                                                    <div className="form-control-plaintext">{selectedScannedItem.barcode}</div>
                                                                </div>
                                                                </div>
                                                            </div>
        
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div className="d-flex align-items-center">
                                                                <label htmlFor="custom_quantity" className="form-label mb-0 me-2"><strong>Quantity:</strong></label>
                                                                    <input
                                                                    type="number"
                                                                    id="custom_quantity"
                                                                    name="custom_quantity"
                                                                    min={1}
                                                                    value={customQuantity}
                                                                    onChange={(e) => setCustomQuantity(e.target.value)}
                                                                    className="form-control form-control-sm"
                                                                    style={{ width: '100px' }}
                                                                    />
                                                                </div>
        
                                                                <button type="submit" className="btn btn-dark btn-sm ms-3">
                                                                Save Qauntity
                                                                </button>
                                                            </div>
                                                            </form>
                                                        ) : (
                                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                            <span style={{ marginRight: '10px' }}>Scan</span>
                                                            <input
                                                            ref={scanRef}
                                                            id="scan_code"
                                                            name="scan_code"
                                                            type="text"
                                                            autoComplete="off"
                                                            className="form-control form-control-md w-25"
                                                            style={{ margin: 0 }}
                                                            onKeyDown={(e) => handleScanKeyDown(e, selectedNormalIBT.doc_num)}
                                                            />
                                                        </div>
                                                        )}
                                                    </>
                                                    )}
                                            </td>
                                        </tr>
                                    </>
                                }       
                            </tbody>
                   </table>
                  { /* Internal IBT Table  End */ }

                       {normalIBTLineState && viewMode !== "default" && viewMode !== "print" &&
                        <table className="table table-bordered">
                            <thead>
                            <tr>
                                <th style={thStyle}>Line No</th>
                                <th style={thStyle}>Item Name</th>
                                <th style={thStyle}>Item Code</th>
                                <th style={thStyle}>Item Barcode</th>
                                {selectedNormalIBT?.status !== "OPEN" && <th style={{...thStyle, width: '112px'}}>QTY On Hand</th>}
                                <th style={{...thStyle, width: '134px'}}>QTY Scanned Out</th>
                                {selectedNormalIBT?.status !== "OPEN" && (
                                <>
                                    <th style={{...thStyle, width: '100px'}}>QTY Sent</th>
                                    <th style={{...thStyle, width: '130px'}}>QTY Scanned In</th>
                                    <th style={{...thStyle, width: '120px'}}>QTY Recieved</th>
                                </>
                                )}
                                {selectedNormalIBT?.status === "OPEN" && (
                                 <th style={thStyle}>Actions</th>
                                )}
                            </tr>
                            </thead>
                            <tbody>
                            {loading ? (
                                <tr>
                                <td
                                    colSpan={selectedNormalIBT?.status === "OPEN" ? 7 : 9}
                                    className="text-center"
                                >
                                    <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                    </div>
                                </td>
                                </tr>
                            ) : (
                                normalIBTLineState.map((normalIBTLine: INormalIBTLine) => {

                                const isEditing = editingRowId === normalIBTLine.id;
                                const {
                                    qty_scanned_out,
                                    qty_sent,
                                    qty_scanned_in,
                                    qty_received,
                                    qty_on_hand
                                } = normalIBTLine;

                                const hasValidationError =
                                    selectedNormalIBT?.status !== "OPEN" &&
                                    (
                                    // Left-side checks: only compare if both are not null
                                    (qty_sent != null && qty_scanned_out != null && qty_sent !== qty_scanned_out) ||
                                    (qty_sent != null && qty_scanned_in != null && qty_scanned_in !== qty_sent) ||
                                    (qty_scanned_in != null && qty_received != null && qty_received !== qty_scanned_in) ||

                                    // Right-side checks: always compare, even if null
                                    qty_sent !== qty_scanned_out ||
                                    qty_scanned_out !== qty_scanned_in ||
                                    qty_scanned_in !== qty_received
                                    );

                                return (
                                    <tr key={normalIBTLine.id} style={{ backgroundColor: hasValidationError ? '#f7f7cd' : 'white' }}>
                                    <td>{normalIBTLine.line_num}</td>
                                    <td>{normalIBTLine.item_name}</td>
                                    <td>{normalIBTLine.item_code}</td>
                                    <td>{normalIBTLine.item_barcode}</td>
                                    {selectedNormalIBT?.status !== "OPEN" && <td className="text-end">{qty_on_hand}</td>}
                                    <td className="text-end">
                                        {isEditing ? (
                                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                style={{ maxWidth: "80px", fontSize: "0.85rem", padding: "0.25rem" }}
                                                value={inputDraft[normalIBTLine.id] ?? ''}
                                                onChange={(e) => handleQtyDraftChange(normalIBTLine.id, e.target.value)}
                                                onBlur={() => handleBlur(normalIBTLine.id)}
                                                autoFocus
                                                onClick={(e) => (e.target as HTMLInputElement).select()}
                                                onFocus={(e) => (e.target as HTMLInputElement).select()}
                                                />
                                            </div>
                                        ) : (
                                            qty_scanned_out
                                        )}
                                    </td>
                                    {selectedNormalIBT?.status !== "OPEN" && (
                                        <>
                                        <td className="text-end">{qty_sent}</td>
                                        <td className="text-end">{qty_scanned_in}</td>
                                        <td className="text-end">{qty_received}</td>
                                        </>
                                    )}
                                     {selectedNormalIBT?.status === "OPEN" && (
                                           <td className='d-flex gap-1'>
                                            {isEditing ? (
                                                <>
                                                { !inputDraft[normalIBTLine.id]?.trim() ? (
                                                        <DisabledSaveQtyBtn/>
                                                    ) : (
                                                        <button
                                                        className="btn btn-secondary"
                                                        onClick={() => handleSaveClick(normalIBTLine.id)}
                                                        title="Save Quantity"
                                                        >
                                                        <Save style={{ width: "20px", height: "20px" }} />
                                                        </button>
                                                    )
                                                }
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() => setEditingRowId(null)}
                                                    title="Cancel"
                                                >
                                                    <X style={{ width: "20px", height: "20px" }} />
                                                </button>
                                                </>
                                            ) : editingRowId === null ? (
                                                <button
                                                className="btn btn-secondary"
                                                onClick={() =>
                                                    handleEditClick(
                                                    normalIBTLine.id,
                                                    normalIBTLine.qty_scanned_out ?? 0
                                                    )
                                                }
                                                title="Edit Quantity"
                                                >
                                                <Pencil style={{ width: "20px", height: "20px" }} />
                                                </button>
                                            ) : (<DisabledEditQtyBtn/>) }

                                            </td>
                                            )}
                                    </tr>
                                  );
                                })
                            )}
                            </tbody>
                        </table>
                        }


                     { selectedNormalIBT && viewMode === 'print' && 
                       <div>
                          <h5 className="mt-4 mb-2 text-uppercase text-secondary fw-bolder border-bottom pb-1 d-flex justify-content-between align-items-center">
                            <span>Select Print Option:</span>
                          </h5>
                          <div className='d-flex gap-1'>
                            <button onClick={() => setPrintTarget('label')}  className={`btn ${printTarget === 'label' ? 'btn-primary' : 'btn-secondary'}`}>Label</button>
                            <button onClick={() => setPrintTarget('manifest')} className={`btn ${printTarget === 'manifest' ? 'btn-primary' : 'btn-secondary'}`}>Manifest</button>
                          </div>
                        </div>
                     }


                       { selectedNormalIBT && viewMode === 'print' && (
                        <>
                            <h5 className="mt-4 mb-2 text-uppercase text-secondary fw-bolder border-bottom pb-1 d-flex justify-content-between align-items-center">
                            <span>{printTarget === 'label' ? 'Print Label' : 'Print Manifest'}</span>
                            <button
                                className="btn btn-secondary d-flex align-items-center gap-1"
                                title={printTarget === 'label' ? 'Print Label' : 'Print Manifest'}
                                onClick={handlePrint}
                            >
                                <Printer style={{ width: '20px', height: '20px' }} />
                            </button>
                            </h5>
    
                            <div className="preview-print-container">
                            {printTarget === 'label' && (
                                <div id="print-label" className="print-sticker">
                                {/* Top Row: Logo + Info */}
                                    <div className="store-logo-section">
                                    {/* Logo */}
                                    <div className='store-logo'>
                                        <img src={customerLogo} style={{ width: customerLogoWidth }} />
                                    </div>
    
                                    {/* Info */}
                                    <div className='store-ref-section'>
                                        <p style={{ fontWeight: 'bold', margin: 0, fontSize: "18px" }}>Send Stock (IBT)</p>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                        <span style={{ fontWeight: 'bold' }}>Ref:</span>
                                        <span>{selectedNormalIBT.doc_num}</span>
                                        </div>
                                    </div>
                                    </div>
    
                                    {/* Warehouse Details */}
                                    <div style={{ marginTop: '10px', marginBottom: '10px', width: '100%' }}>
                                    {[
                                        { label: 'From Warehouse:', value: store.name},
                                        { label: 'To Warehouse:', value: selectedToWharehouseStore?.name},
                                        { label: 'Total Items:', value: totalQtySent},
                                        { label: 'Shop Unit Number:', value: addressLines[0] || '' },
                                        { label: 'Street Address:', value: addressLines[1] || '' },
                                        { label: 'Postal Code:', value: addressLines[2] || '' },
                                        { label: 'Area:', value: addressLines[3] || '' },
                                    ].map(({ label, value }) => (
                                        <div key={label}
                                        style={{ display: 'flex',  gap: '4px', flexWrap: 'wrap', ...(label === 'Shop Unit Number:' ? { marginTop: '12px' } : {}), }}
                                        >
                                        <span style={{ fontWeight: 'bold' }}>{label}</span>
                                        <span>{value}</span>
                                        </div>
                                    ))}
                                    </div>
    
                                    {/* Notes */}
        
                                    <div className="notes-section">
                                    <p style={{ margin: 0 }}>Notes: {selectedNormalIBT.notes}</p>
                                    </div>
                                </div>
                            )}
    
                            {printTarget === 'manifest' && (
                                <div id="print-label" className="print-sticker">
                                {/* Manifest content goes here */}
                                {/* Top Row: Logo + Info */}
                                    <div className="store-logo-section">
                                    {/* Logo */}
                                    <div className='store-logo'>
                                        <img src={customerLogo} style={{ width: customerLogoWidth }} />
                                    </div>
    
                                    {/* Info */}
                                    <div className='store-ref-section'>
                                        <p style={{ fontWeight: 'bold', margin: 0, fontSize: "18px" }}>Send Stock (IBT)</p>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                        <span style={{ fontWeight: 'bold' }}>Ref:</span>
                                        <span>{selectedNormalIBT.doc_num}</span>
                                        </div>
                                    </div>
                                    </div>
    
                                    {/* Warehouse Details */}
                                   <div style={{ marginTop: '10px', marginBottom: '10px', width: '100%' }}>
                                    {[
                                    { label: 'From Warehouse:', value: store.name},
                                    { label: 'To Warehouse:', value: selectedToWharehouseStore?.name},
                                    { label: 'Total Items:', value: totalQtySent},
                                    { label: 'Shop Unit Number:', value: addressLines[0] || '' },
                                    { label: 'Street Address:', value: addressLines[1] || '' },
                                    { label: 'Postal Code:', value: addressLines[2] || '' },
                                    { label: 'Area:', value: addressLines[3] || '' },
                                    ].map(({ label, value }) => (
                                    <div key={label}
                                      style={{ display: 'flex',  gap: '4px', flexWrap: 'wrap', ...(label === 'Shop Unit Number:' ? { marginTop: '12px' } : {}), }}
                                    >
                                      <span style={{ fontWeight: 'bold' }}>{label}</span>
                                      <span>{value}</span>
                                    </div>
                                  ))}
                                 </div>
    
    
                                    {/* Adding the Items: label */}
                                <div style={{ marginTop: '12px', width: '100%' }}>
                                    <span style={{ fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Items Manifest:</span>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
                                    <thead>
                                        <tr>
                                        <th style={{ textAlign: 'left', padding: '4px', borderBottom: '1px solid #ccc' }}>Code</th>
                                        <th style={{ textAlign: 'left', padding: '4px', borderBottom: '1px solid #ccc' }}>Name</th>
                                        <th style={{ textAlign: 'right', padding: '4px', borderBottom: '1px solid #ccc' }}>Quantity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {normalIBTLineState.map((normalIBTLine: INormalIBTLine) => (
                                        <tr key={normalIBTLine.id}>
                                            <td style={{ padding: '4px 8px' }}>{normalIBTLine.item_code}</td>
                                            <td style={{ padding: '4px 8px' }}>{normalIBTLine.item_name}</td>
                                            <td style={{ padding: '4px 8px', textAlign: 'right' }}>{normalIBTLine.qty_sent}</td>
                                        </tr>
                                        ))}
                                    </tbody>
                                    </table>
                                </div>
    
                                    {/* Notes */}
        
                                    <div className="notes-section">
                                    <p style={{ margin: 0 }}>Notes: {selectedNormalIBT.notes}</p>
                                    </div>
                                </div>
                            )}
                            </div>
                        </>
                        )}

             </div>
        </CardBody>

       {/* Modal Create Normal IBT - Start */}
            <>
              <Modal show={showCreateNormalIBTModal} onHide={resetNormalIBTModal}>
                  <Modal.Header closeButton>
                      <Modal.Title>{`Creating Normal IBT [${store.name}]`}</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <div className='informational-message-section'>
                        <strong>Note:</strong> You can proceed without uploading a file. Simply click 
                        <strong> Create </strong> to skip this step and scan your items directly in the interface. 
                            The system will guide you through entering or capturing the necessary information on the next screen.
                   </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                     <label htmlFor="file_category" style={{ marginBottom: '6px', fontWeight: 'bold', marginLeft: '4px' }}>
                          Select Store Option:
                          <span style={{ color: '#c0392b', fontWeight: 'normal', marginLeft: '8px' }}>
                              * Required
                          </span>
                      </label>
                      <select
                          ref={storeRef}
                          id="normal_IBT_store"
                          name="normal_IBT_store"
                          className="form-control form-control-md w-80 mb-2"
                          value={selectedStore}
                          onChange={(e) => setSelectedStore(e.target.value)}
                       >
                          <option value="">-- Select Store --</option>
                          {stores && store && stores
                            .filter(s => s.id !== store.id)
                            .map(s => (
                                <option key={s.id} value={s.code}>
                                {s.name}
                                </option>
                            ))}
                      </select>
                      <label htmlFor="file_codes" style={{ marginBottom: '6px', fontWeight: 'bold', marginLeft: '4px' }}>
                          Upload File:
                          <span style={{ color: '#c0392b', fontWeight: 'normal', marginLeft: '8px' }}>
                              * Optional 
                          </span>
                      </label>
                      <input ref={fileRef} id="file_codes" name="file_codes" type="file" className="form-control form-control-md w-80" onChange={handleFolderChange} />
                      <label htmlFor="file_codes" style={{ marginBottom: '6px', fontWeight: 'bold', marginLeft: '4px', marginTop: '8px' }}>
                            Leave Notes:
                          <span style={{ color: '#c0392b', fontWeight: 'normal', marginLeft: '8px' }}>
                              * Optional 
                          </span>
                      </label>
                      <textarea
                          id="notes"
                          name="notes"
                          className="form-control form-control-md w-80"
                          value={normalIBTNotes}
                          onChange={(e) => setNormalIBTNotes(e.target.value)}
                      />
                  </div>
                  </Modal.Body>
                  <Modal.Footer>
                  <Button variant="primary" disabled={!selectedStore} onClick={() => {  setShowCreateNormalIBTModal(false); handleCreateNormalIBTSubmit(); } }>
                      { selectedFile ? "Upload" : " Create" } 
                  </Button>
                  <Button variant="secondary" onClick={resetNormalIBTModal}>
                      Cancel
                  </Button>
                  </Modal.Footer>
              </Modal>
            </> 
          {/* Modal Create Normal IBT  - End */}


            {/* Modal To Send Internal IBT - Start */}
            <>
            <Modal show={showUpdateStatusNormalIBTModal} onHide={() => { setShowUpdateStatusNormalIBTModal(false); }}>
                <Modal.Header closeButton>
                    <Modal.Title>
                       {`Send Normal IBT [${selectedDocNum}]`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                    <p>
                     { "Are you sure you would like to send this normal IBT?"}
                    </p>
                </div>
                </Modal.Body>
                <Modal.Footer>
                <Button variant="primary" onClick={() => { setShowUpdateStatusNormalIBTModal(false); handleToSendNormalIBTSubmit();  }}>
                    Confirm
                </Button>
                <Button variant="secondary" onClick={() => { setShowUpdateStatusNormalIBTModal(false); } }>
                    Cancel
                </Button>
                </Modal.Footer>
            </Modal>
            </> 
         {/* Modal To Send Internal IBT - End */}


         {/* Modal Delete Normal IBT - Start */}
            <>
            <Modal show={showDeleteNormalIBTModal} onHide={() => { setShowDeleteNormalIBTModal(false); }}>
                <Modal.Header closeButton>
                    <Modal.Title>{`Delete Normal IBT [${selectedDocNum}]`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                    <p>Are you sure you would like to delete this normal IBT?</p>
                </div>
                </Modal.Body>
                <Modal.Footer>
                <Button variant="primary" onClick={() => { setShowDeleteNormalIBTModal(false); handleDeleteNormalIBTSubmit();  }}>
                    Confirm
                </Button>
                <Button variant="secondary" onClick={() => { setShowDeleteNormalIBTModal(false); } }>
                    Cancel
                </Button>
                </Modal.Footer>
            </Modal>
            </> 
        {/* Modal Delete Normal IBT - End */}
         
          

   </Card>
  </>
  )
}
